import React, { memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import PropTypes from 'prop-types';
import { Box, Heading, NakedButton, Flex, Text } from 'roo-ui/components';
import ImageGallery from 'components/ImageGallery';
import Offer from 'pages/Property/LegacyPropertyAvailability/RoomTypes/RoomTypeList/Offer';
import Row from 'components/Row';
import Markup from 'components/Markup';
import Modal from 'components/Modal';
import FacilitiesList from 'components/FacilitiesList';
import TextButton from 'components/TextButton';
import { useModal } from 'lib/hooks';
import { useDataLayer } from 'hooks/useDataLayer';
import theme from 'lib/theme';
import { mediaQuery } from 'lib/styledSystem';
import RoomTypeDisplayMeta from './components/RoomTypeDisplayMeta';
import ImageGalleryThumbnail from 'pages/Property/ImageGalleryThumbnail';

const RoomTypeWrapper = styled(Box)`
  padding-top: ${themeGet('space.10')};
  border-top: ${themeGet('borders.1')} ${themeGet('colors.greys.alto')};
  margin-bottom: ${themeGet('space.10')};

  &:first-of-type {
    padding-top: 0;
    border-top: 0;
    margin-top: 0;
  }
`;

const CloseButton = styled(NakedButton)`
  position: absolute;
  right: ${themeGet('space.6')};
  top: ${themeGet('space.6')};
  bottom: auto;

  &:focus {
    outline: none;
  }
`;

CloseButton.displayName = 'CloseButton';

const FullScreenPane = styled(Flex)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${themeGet('colors.greys.charcoal')};
  z-index: ${themeGet('zIndices.modal')};
  padding: 5%;

  ${mediaQuery.minWidth.sm} {
    padding: 10% 20%;
  }
`;

FullScreenPane.displayName = 'FullScreenPane';

const UnavailableMessage = ({ children }) => (
  <Flex alignItems="center" mt={[4, 0]}>
    <Text fontWeight="bold" fontSize="base" color="greys.dusty" mr={4}>
      {children}
    </Text>
  </Flex>
);

UnavailableMessage.propTypes = {
  children: PropTypes.node.isRequired,
};

const RoomType = ({ roomType, hasQuery }) => {
  const { openModal, modalProps } = useModal();
  const { emitInteractionEvent } = useDataLayer();
  const { name, description, offers, roomTypeFacilities, maxOccupantCount } = roomType;
  const imageUrl = roomType?.mainImage?.urlMedium;
  const caption = roomType?.mainImage?.caption;
  const roomImages = roomType?.images || [];
  const hasImages = roomImages.length > 0;
  const hasOffers = offers && offers.length;
  const thumbnailSize = theme.imageSizes.legacyRoomTypeThumbnail;

  const handleViewRoomDetails = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'View Room Details Pop Up', value: 'Link Selected' });
  }, [emitInteractionEvent, openModal]);

  const handleOpenGallery = useCallback(() => {
    emitInteractionEvent({ type: 'Legacy Room Type Gallery', value: 'Gallery Opened' });
  }, [emitInteractionEvent]);

  const handleGalleryImageChanged = useCallback(
    (id) => {
      emitInteractionEvent({ type: 'Legacy Room Type Gallery', value: `Gallery Scrolled to ${id}` });
    },
    [emitInteractionEvent],
  );

  return (
    <RoomTypeWrapper>
      <Row spacing="10px">
        <Flex flexDirection={['column', 'row']} px={[2, 0]}>
          {!hasImages && (
            <Flex
              height={thumbnailSize.height}
              width={thumbnailSize.width}
              borderRadius="default"
              bg="greys.alto"
              alignItems="center"
              justifyContent="center"
              color="greys.steel"
            >
              Image not available
            </Flex>
          )}
          {hasImages && (
            <Box position="relative" height={thumbnailSize.height} width={thumbnailSize.width}>
              <ImageGallery
                images={roomImages}
                height={thumbnailSize.height}
                width={thumbnailSize.width}
                onClick={handleOpenGallery}
                onImageChanged={handleGalleryImageChanged}
                imageSize="medium"
                hideThumbnails
              >
                <ImageGalleryThumbnail
                  height={thumbnailSize.height}
                  width={thumbnailSize.width}
                  src={imageUrl}
                  alt={caption || name}
                  borderRadius="default"
                  overflow="hidden"
                  imageCount={roomImages.length}
                  lazy
                />
              </ImageGallery>
            </Box>
          )}
          <Flex flex="1 1 auto" mt={[2, 0]} pl={[0, 5]} justifyContent="space-between" flexDirection={['column', 'row']}>
            <Box>
              <Heading.h2 fontSize={['md', 'lg']}>{name}</Heading.h2>
              <RoomTypeDisplayMeta maxOccupantCount={maxOccupantCount} />
              <TextButton
                onClick={handleViewRoomDetails}
                color="brand.primary"
                fontSize="base"
                aria-label={`View room details for ${name}`}
              >
                View room details
              </TextButton>
            </Box>
            {!hasQuery && <UnavailableMessage>Select stay dates to show prices</UnavailableMessage>}
            {hasQuery && !hasOffers && <UnavailableMessage>Unavailable</UnavailableMessage>}
          </Flex>
        </Flex>
        <Modal {...modalProps} title="Room details">
          <Box mb={8} data-testid="room-description">
            <Markup content={description} />
          </Box>
          <Heading.h4 mb={4}>Room facilities</Heading.h4>
          <FacilitiesList facilities={roomTypeFacilities} />
        </Modal>
      </Row>

      {hasOffers && (
        <Box mt={6}>
          {offers.map((offer, counter) => (
            <Offer roomTypeId={roomType.id} offer={offer} counter={counter + 1} key={offer.id} roomName={name} />
          ))}
        </Box>
      )}
    </RoomTypeWrapper>
  );
};

RoomType.propTypes = {
  roomType: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    roomTypeFacilities: PropTypes.array.isRequired,
    offers: PropTypes.array,
    maxOccupantCount: PropTypes.number,
    images: PropTypes.array,
    mainImage: PropTypes.shape({
      urlMedium: PropTypes.string,
      caption: PropTypes.string,
    }),
  }).isRequired,
  hasQuery: PropTypes.bool,
};

RoomType.defaultProps = {
  hasQuery: false,
};

export default memo(RoomType);
